import React, { useState } from 'react';
import TrebbleButton from '../common/TrebbleButton';
import TrebbleInput from '../common/TrebbleInput';
import TrebbleTooltip from '../common/TrebbleTooltip';
import TrebbleCard from '../common/TrebbleCard';
import TrebbleSkeleton from '../common/TrebbleSkeleton';
import TrebbleAvatar from '../common/Avatar';
import ModalHeader from '../common/ModalHeader';
import { GuestAccessLevel, GuestInfo } from '../../models/collaboration/types/GuestTypes';

const AVATAR_COLORS = ['#FF6B35', '#4A90E2', '#7B61FF', '#27AE60', '#E67E22', '#16A085'];

const getInitials = (nameOrEmail: string): string => {
    const source = (nameOrEmail || '').trim();
    if (!source) return '?';
    const atIdx = source.indexOf('@');
    const base = atIdx > 0 ? source.slice(0, atIdx) : source;
    const parts = base.split(/[.\s_+-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 2).toUpperCase();
};

const getAvatarColor = (key: string): string => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = ((hash << 5) - hash) + key.charCodeAt(i);
        hash |= 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface ProjectShareContentProps {
    projectId: string;
    projectName: string;
    currentGuests: GuestInfo[];
    canInviteMoreGuests: boolean;
    isLoading: boolean;
    guestLoadErrorMessage?: string | null;
    onInvite: (email: string, accessLevel: GuestAccessLevel) => Promise<void>;
    onRevoke: (guestRecordId: string) => Promise<void>;
    onClose: () => void;
    i18nModule: Record<string, string>;
}

const ProjectShareContent: React.FC<ProjectShareContentProps> = ({
    currentGuests,
    canInviteMoreGuests,
    isLoading,
    guestLoadErrorMessage,
    onInvite,
    onRevoke,
    onClose,
    i18nModule,
}) => {
    const [email, setEmail] = useState('');
    const [accessLevel] = useState<GuestAccessLevel>(GuestAccessLevel.CanView);
    const [isInviting, setIsInviting] = useState(false);
    const [revokingGuestId, setRevokingGuestId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const t = (key: string): string => {
        return (window as unknown as Record<string, Function>).getI18n(i18nModule, key);
    };

    const handleInvite = async () => {
        if (!email.trim()) {
            return;
        }
        setIsInviting(true);
        setErrorMessage(null);
        try {
            await onInvite(email.trim(), accessLevel);
            setEmail('');
        } catch (error: unknown) {
            if (error && typeof error === 'object' && (error as { handledByUpgradePrompt?: boolean }).handledByUpgradePrompt) {
                return;
            }
            const message = error instanceof Error ? error.message : t('INVITE_ERROR');
            setErrorMessage(message);
        } finally {
            setIsInviting(false);
        }
    };

    const handleRevoke = async (guestRecordId: string) => {
        setRevokingGuestId(guestRecordId);
        setErrorMessage(null);
        try {
            await onRevoke(guestRecordId);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('REVOKE_ERROR');
            setErrorMessage(message);
        } finally {
            setRevokingGuestId(null);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && email.trim()) {
            handleInvite();
        }
    };

    const canSendInvite = canInviteMoreGuests;
    const showInviteDisabledState = !isLoading && !canSendInvite;
    const inviteDisabledTooltip = showInviteDisabledState
        ? (guestLoadErrorMessage || t('INVITE_GUEST_DISABLED_TOOLTIP'))
        : '';

    return (
        <TrebbleCard
            useEditorTheme={false}
            className="p-6 min-w-[320px]"
        >
            <div className="flex flex-col gap-5">
                <ModalHeader
                    title={t('SHARE_PROJECT')}
                    marginBottom={0}
                />

                {/* Who has access section */}
                <div>
                    <div className="text-sm font-medium mb-3">
                        {t('WHO_HAS_ACCESS')}
                    </div>

                    {isLoading ? (
                        <TrebbleSkeleton
                            active
                            paragraph={{ rows: 2, width: ['80%', '60%'] }}
                            useEditorTheme={false}
                        />
                    ) : guestLoadErrorMessage && currentGuests.length === 0 ? (
                        <div className="text-[13px] text-[#ff4d4f] py-2">
                            {guestLoadErrorMessage}
                        </div>
                    ) : currentGuests.length === 0 ? (
                        <div className="text-[13px] opacity-65 py-2">
                            {t('NO_GUESTS_YET')}
                        </div>
                    ) : (
                        <div className="flex flex-col">
                            {currentGuests.map((guest, idx) => {
                                const guestRecordId = guest.guestRecordId || guest.guestId || guest.email;
                                const canRevokeGuest = Boolean(guest.guestRecordId || guest.guestId);
                                const isLast = idx === currentGuests.length - 1;
                                const avatarKey = guest.email || guest.name || guestRecordId;
                                const primaryLine = guest.name || guest.email;
                                const secondaryLine = guest.name
                                    ? guest.email
                                    : (guest.status === 'invited' ? t('INVITE_PENDING') : '');
                                return (
                                    <div
                                        key={guestRecordId}
                                        className={`flex items-center gap-3 py-2.5 ${isLast ? '' : 'border-b border-[rgba(128,128,128,0.2)]'}`}
                                    >
                                        {guest.profileImageUrl ? (
                                            <TrebbleAvatar
                                                useEditorTheme={false}
                                                size={36}
                                                src={guest.profileImageUrl}
                                                className="shrink-0"
                                            />
                                        ) : (
                                            <TrebbleAvatar
                                                useEditorTheme={false}
                                                size={36}
                                                className="shrink-0 text-white text-[13px] font-semibold"
                                                style={{ backgroundColor: getAvatarColor(avatarKey) }}
                                            >
                                                {getInitials(guest.name || guest.email)}
                                            </TrebbleAvatar>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                                                {primaryLine}
                                            </div>
                                            {secondaryLine && (
                                                <div className="text-xs opacity-65 overflow-hidden text-ellipsis whitespace-nowrap">
                                                    {secondaryLine}
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-xs opacity-75 px-2.5 py-[3px] bg-[rgba(128,128,128,0.15)] shrink-0">
                                            {guest.accessLevel === GuestAccessLevel.CanEdit ? t('CAN_EDIT') : t('CAN_VIEW')}
                                        </span>
                                        <TrebbleButton
                                            aria-label={t('REVOKE_ACCESS')}
                                            size="small"
                                            type="text"
                                            onClick={() => canRevokeGuest && handleRevoke(guestRecordId)}
                                            useEditorTheme={false}
                                            loading={revokingGuestId === guestRecordId}
                                            disabled={revokingGuestId !== null || !canRevokeGuest}
                                            className="shrink-0 px-2 py-1"
                                        >
                                            <span className="pe-7s-trash text-lg" />
                                        </TrebbleButton>
                                    </div>
                                );
                            })}
                            {guestLoadErrorMessage && (
                                <div className="text-[#ff4d4f] text-xs pt-2">
                                    {guestLoadErrorMessage}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Invite section */}
                <div className="border-t border-[rgba(128,128,128,0.2)] pt-4">
                    <div className="text-sm font-medium mb-2">
                        {t('INVITE_GUEST')}
                    </div>

                    <TrebbleTooltip
                        title={inviteDisabledTooltip}
                        useEditorTheme={false}
                    >
                        <div className={showInviteDisabledState ? 'opacity-50' : 'opacity-100'}>
                            <TrebbleInput
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t('GUEST_EMAIL_PLACEHOLDER')}
                                useEditorTheme={false}
                                disabled={isInviting || isLoading || !canSendInvite}
                                className="mb-1.5"
                            />
                            <div className="text-xs opacity-65">
                                {t('GUEST_INVITE_HINT')}
                            </div>
                        </div>
                    </TrebbleTooltip>
                </div>

                {/* Error message */}
                {errorMessage && (
                    <div className="text-[#ff4d4f] text-[13px]">
                        {errorMessage}
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3 justify-end">
                    <TrebbleButton
                        onClick={onClose}
                        useEditorTheme={false}
                        disabled={isInviting}
                    >
                        {t('REVOKE_CONFIRM_CANCEL')}
                    </TrebbleButton>
                    <TrebbleButton
                        type="primary"
                        onClick={handleInvite}
                        useEditorTheme={false}
                        loading={isInviting}
                        disabled={isLoading || !email.trim() || !canSendInvite}
                    >
                        {t('SEND_INVITE')}
                    </TrebbleButton>
                </div>
            </div>
        </TrebbleCard>
    );
};

export { ProjectShareContent };
export type { ProjectShareContentProps };
